/**
 * This page has three main responsibilities:
 * 1. Inspect the CF-IPCountry header to determine if the user is in a country
 *   that is allowed to sign up for the service. Redirect that user to the correct sub path to complete the signup process
 *   ie: US users will be redirected to /signup/us
 *
 * 2. Render the signup footer available to all users and pages
 * 3. Redirect the user if they are already authenticated
 * */

import type { LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { isAuthenticatedRedirect } from "~/services/auth.server";
import { redirect } from "@remix-run/node";
import {  Outlet } from "@remix-run/react";
import {
  getSignupSession,
  commitSignupSession,
} from "~/services/signupSession.server";
import { defaultMetaTagsBuilder } from "~/utils/meta";

const genericSignupScreens = ["names", "email-password", "genderDob"];

export const meta: MetaFunction = ({ location }) => {
  const url = new URL(location.pathname, "https://app.7qz.io");
  return defaultMetaTagsBuilder({
    title: "Sign-Up",
    description: "Create your 7Qz account",
    url: url,
  });
};

export async function loader({ request }: LoaderFunctionArgs) {
  await isAuthenticatedRedirect(request);

  // TODO add country tracker
  // check to see if the request url is exactly /signup
  // if it is, redirect the user to the correct sub path
  const url = new URL(request.url);
  const redirectQuery = new URLSearchParams(url.search).get("redirect");

  if (url.pathname === "/signup") {
    const session = await getSignupSession(request.headers.get("Cookie"));

    // the session has no data set, so initialize it and then redirect to the first screen in the generic signup flow
    if (Object.keys(session.data).length === 0) {
      // initialize the session
      session.set("flow", genericSignupScreens);
      session.set("incompleteScreens", genericSignupScreens);
      // redirect to the first signup screen for the country
      return redirect(`/signup/${genericSignupScreens[0]}`, {
        headers: {
          "Set-Cookie": await commitSignupSession(session),
        },
      });
    }

    const incompleteScreens = session.get("incompleteScreens") || [];

    if (incompleteScreens.length === 0) {
      // redirect to the homepage
      return redirect("/");
    }

    return redirect(
      redirectQuery
        ? `/signup/${incompleteScreens[0]}?redirect=${redirectQuery}`
        : `/signup/${incompleteScreens[0]}`
    );
  }
  return null;
}

export default function Signup() {
  return <Outlet />
}
